import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import AuthModal from './AuthModal';

export default function Navigation() {
  const { user, logout, isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileOpen(false);
  };

  const navLinks = [
    { to: '/', label: 'Home', testId: 'nav-home' },
    { to: '/about', label: 'About', testId: 'nav-about' },
    { to: '/services', label: 'Services', testId: 'nav-services' },
    { to: '/report-case', label: 'Report Case', testId: 'nav-report' },
    { to: '/contact', label: 'Contact', testId: 'nav-contact' },
  ];

  return (
    <>
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-28">
            <Link to="/" className="flex items-center">
              <img 
                src={`${process.env.PUBLIC_URL}/logos/crystalintelligence-logo.svg`} 
                alt="Crystal Intelligence" 
                className="h-20 md:h-20 lg:h-20 w-auto"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map(link => (
                <Link key={link.to} to={link.to} className="text-slate-300 hover:text-white transition-smooth" data-testid={link.testId}>{link.label}</Link>
              ))}
            </div>

            {/* Desktop auth buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Button
                    onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/dashboard')}
                    data-testid="dashboard-button"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {user?.role === 'admin' ? 'Admin' : 'Dashboard'}
                  </Button>
                  <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <Button onClick={() => setShowAuthModal(true)} data-testid="get-started-button">
                  Get Started
                </Button>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-1 text-slate-300 hover:text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="md:hidden bg-card/95 backdrop-blur-xl border-t border-border">
            <div className="px-4 py-4 space-y-1">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-muted rounded-lg transition-colors"
                  data-testid={`mobile-${link.testId}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 border-t border-border mt-3 space-y-2">
                {isAuthenticated ? (
                  <>
                    <Button
                      className="w-full justify-start"
                      onClick={() => { navigate(user?.role === 'admin' ? '/admin' : '/dashboard'); setMobileOpen(false); }}
                    >
                      <User className="h-4 w-4 mr-2" />
                      {user?.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <Button className="w-full" onClick={() => { setShowAuthModal(true); setMobileOpen(false); }}>
                    Get Started
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}
